import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { TeamPermissionsStateService } from '@brand-lolly/app/user-management/team/services/team-permissions-state.service';
import {
	TeamAction,
	teamActionAllowed
} from '@brand-lolly/app/user-management/team/types/team.types';

/**
 * Where a member who asked for a billing address is sent instead.
 *
 * Their own credit statement, not the workspace root and not a refusal page:
 * what they were after is almost always "how much is left", and that page shows
 * them their own balance and their own spending — which is precisely the half of
 * billing that IS theirs.
 */
const MEMBER_FALLBACK_ROUTE = '/user-management/credits';

/**
 * Close a route that changes what the ACCOUNT pays to anyone but its owner.
 *
 * Hiding the controls is not enough on its own — the addresses are typed, linked
 * from upsell banners all over the app, and bookmarked — so the same matrix that
 * greys a control also closes the route. `billing.manage` is one action covering
 * the whole surface (plan changes, credit purchases, payment method,
 * cancellation), which is why it carries no method and no path on the server: it
 * greys a GROUP of controls rather than keying off one call.
 *
 * FAIL-OPEN ON AN UNREADABLE MATRIX. See
 * {@link TeamPermissionsStateService} — the `/team` tree is not deployed
 * everywhere this app runs, and a gate that closed on a 404 would lock every
 * owner out of their own billing page. A matrix that WAS read is obeyed.
 */
export const billingOwnerGuard: CanActivateFn = ():
	| Observable<boolean | UrlTree>
	| boolean
	| UrlTree => {
	const permissionsState = inject(TeamPermissionsStateService);
	const router = inject(Router);

	return permissionsState.ensureLoaded().pipe(
		map(permissions =>
			teamActionAllowed(permissions, TeamAction.BILLING_MANAGE)
				? true
				: router.parseUrl(MEMBER_FALLBACK_ROUTE)
		),
		catchError(() => of(true))
	);
};
